/**
 * Previsualización del grabado: el cliente coloca su texto sobre la foto.
 *
 * Todo se dibuja en un lienzo, no en SVG, por dos motivos: el texto curvado se
 * resuelve girando cada letra alrededor de un centro —algo que el lienzo hace
 * de forma natural— y, sobre todo, porque la misma función que pinta la
 * pantalla genera la imagen que se adjunta al pedido. Un solo camino de
 * dibujo, así lo que ve el cliente y lo que recibe el taller no pueden
 * desincronizarse.
 *
 * Hay una «escena» por zona grabable:
 *
 *   virola         aro visto desde arriba; las frases que agregue el cliente,
 *                  sin saltos de línea, cada una curva (sigue el aro) o recta
 *                  (derecha, con giro), y un logo que se apoya en la curva
 *                  como los íconos
 *   cuerpo         rectángulo; las frases van rectas, admiten varias líneas y
 *                  se pueden girar; puede acompañarlas el logo del cliente
 *   virola,cuerpo  las dos escenas a la vez: las frases de la virola en el aro
 *                  y las del costado en el costado. Cada zona lleva su propio
 *                  logo: el de la virola (data-logo3) y el del costado
 *                  (data-logo)
 *   bombilla       la caña; el texto va en vertical, en una sola línea
 *   termo          el cuerpo del termo; igual que el cuerpo del mate
 *
 * Las frases llegan en tres listas (data-phrases-p, -c y -s, en JSON): «p» la
 * zona principal de la tarjeta, «c» el costado grabando virola y costado, y
 * «s» el otro costado. Cada frase trae un id fijo, así al quitar una las demás
 * conservan su sitio en la foto.
 *
 * Las figuras de texto no son piezas aparte: se insertan en el propio texto,
 * así el cliente coloca un solo bloque y no tiene que cuadrar varios
 * elementos. Los íconos dibujados (mate, Sol de Mayo, estrellas…), en cambio,
 * sí son piezas: cada uno se mueve y se agranda por su cuenta. En el aro
 * giran siguiendo la curva, como las letras.
 *
 * Cada frase del aro guarda hacia dónde miran sus letras: «out» las aleja del
 * centro y «in» las gira hacia él, que es lo que de verdad distingue un
 * grabado mirando al exterior del mate de uno mirando a su interior. No se
 * deduce de la posición: si se dedujera, el mismo botón haría cosas distintas
 * arriba y abajo del aro.
 *
 * Cuando hay más de una pieza en una escena, el cliente elige cuál está
 * ajustando: con dos frases superpuestas, adivinar cuál se arrastra por
 * cercanía era incómodo y poco predecible.
 *
 * El área grabable la define el taller en porcentaje de la foto, así no
 * depende del tamaño en pantalla ni de la resolución de la imagen:
 *
 *   virola  centroX,centroY,radioInterior,radioExterior
 *   cuerpo  x,y,ancho,alto
 */

const EXPORT_WIDTH = 1400;
const LOGO_SLOT = 9;
/** El logo del otro costado (data-logo2). */
const LOGO2_SLOT = 10;
/**
 * Logo de la virola. Con solo virola es el logo de siempre (data-logo); con
 * virola y costado, el suyo propio (data-logo3), porque el de siempre va al
 * costado.
 */
const RING_LOGO_SLOT = 11;
/** Prefijo de las piezas de ícono: «icon:sol-de-mayo~2» (cada copia, aparte). */
const ICON_PREFIX = 'icon:';
/** Ancho de un ícono a tamaño 100, en proporción al ancho de la foto. */
const ICON_SCALE = 0.25;
/** Giro de los íconos: hasta media vuelta a cada lado; cerca de 0 se endereza. */
const ROTATE_LIMIT = 180;
const ROTATE_SNAP = 4;
const ROTATE_LABEL = 'Giro';
/** Prefijo de las piezas de texto: «t:p:3» es la frase 3 de la lista «p». */
const TEXT_PREFIX = 't:';
/** Posición inicial de cada frase nueva en el aro, en orden: arriba, abajo, derecha, izquierda… */
const RING_ANGLES = [-Math.PI / 2, Math.PI / 2, 0, Math.PI, -Math.PI / 4, -(3 * Math.PI) / 4, Math.PI / 4, (3 * Math.PI) / 4];
/** Y en el costado: altura relativa dentro del área grabable. */
const BODY_ROWS = [0.4, 0.6, 0.2, 0.8, 0.3, 0.7, 0.5, 0.1];

/**
 * La imagen para el taller viaja con el formulario al añadir al carrito, así
 * que su peso es tiempo de espera para el cliente. En JPG pesa una décima
 * parte que en PNG, y todos los navegadores la generan igual. No necesita
 * transparencia: lleva la foto de fondo.
 */
const EXPORT_TYPE = 'image/jpeg';
const EXPORT_QUALITY = 0.9;
const EXPORT_NAME = 'grabado.jpg';

/** Mano que señala, en trazo: la indicación de que el diseño se puede mover. */
const HAND_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" focusable="false">' +
  '<path d="M10 11V4.5a1.5 1.5 0 0 1 3 0V10"/>' +
  '<path d="M13 9.5a1.5 1.5 0 0 1 3 0V11"/>' +
  '<path d="M16 10.5a1.5 1.5 0 0 1 3 0V15a6 6 0 0 1-6 6h-1.5a6 6 0 0 1-4.6-2.2L4.3 15.6a1.5 1.5 0 0 1 2.3-1.9L10 17"/>' +
  '</svg>';

class MrPreview extends HTMLElement {
  #controller = new AbortController();
  #exportTimer = 0;
  /** @type {Map<HTMLElement, object[]>} */
  #items = new Map();
  #dragging = null;
  /** @type {HTMLImageElement | null} */
  #logo = null;
  /** Logo del otro costado. @type {HTMLImageElement | null} */
  #logo2 = null;
  /** Logo de la virola grabando virola y costado. @type {HTMLImageElement | null} */
  #logo3 = null;
  /** Zona en la que va cada ícono elegido: id → «virola» o «cuerpo». */
  #iconZones = new Map();
  /** Dibujos de los íconos ya listos para el lienzo, por id y color. */
  #iconImages = new Map();
  /** Última zona que tocó el cliente: ahí entran los íconos nuevos. */
  #currentZone = '';

  static observedAttributes = [
    'data-phrases-p',
    'data-phrases-c',
    'data-phrases-s',
    'data-font',
    'data-zones',
    'data-logo',
    'data-logo2',
    'data-logo3',
    'data-icons',
  ];

  connectedCallback() {
    const { signal } = this.#controller;

    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.addEventListener('pointerup', this.#handlePointerUp, { signal });
    this.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    this.addEventListener('input', this.#handleControl, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    window.addEventListener('resize', this.#drawAll, { signal });

    for (const image of this.querySelectorAll('.mr-preview__image')) {
      image.addEventListener('load', this.#drawAll, { signal });
    }

    this.#addHints();
    this.#addRotateControls();
    this.#sync();
  }

  /**
   * Deslizador «Giro» debajo de los controles de cada escena. Se ve con un
   * ícono, un logo o una frase recta elegidos: la frase curva sigue el aro.
   */
  #addRotateControls() {
    for (const controls of this.querySelectorAll('.mr-preview__controls')) {
      if (controls.parentElement?.querySelector('[data-mr-rotate]')) continue;

      const label = document.createElement('label');
      label.className = 'mr-preview__control';
      label.dataset.mrRotateWrap = '';
      label.style.display = 'none';

      const name = document.createElement('span');
      name.textContent = ROTATE_LABEL;

      const input = document.createElement('input');
      input.type = 'range';
      input.min = String(-ROTATE_LIMIT);
      input.max = String(ROTATE_LIMIT);
      input.step = '1';
      input.value = '0';
      input.dataset.mrRotate = '';

      label.append(name, input);
      controls.after(label);
    }

    // Curvo / Recto: solo en el aro, donde el texto puede seguir la curva
    const ring = this.querySelector('[data-mr-stage][data-zone="virola"] .mr-preview__controls');
    if (ring && !ring.querySelector('[data-mr-shape]')) {
      const [curve = 'Curvo', straight = 'Recto'] = (this.dataset.shapeLabels || '').split('|').map((label) => label.trim());
      const group = document.createElement('div');
      group.className = 'mr-preview__shape';
      group.dataset.mrShapeWrap = '';
      group.setAttribute('role', 'group');
      group.hidden = true;

      for (const [value, text] of [
        ['curve', curve || 'Curvo'],
        ['straight', straight || 'Recto'],
      ]) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mr-preview__shape-option';
        button.dataset.mrShape = value;
        button.textContent = text;
        group.append(button);
      }

      ring.prepend(group);
    }
  }

  /**
   * Manito en la esquina de cada foto: avisa que el diseño se puede mover.
   * Es decorativa (el lector de pantalla no la anuncia) y no tapa el lienzo:
   * los toques la atraviesan. Se desvanece la primera vez que se toca la foto.
   */
  #addHints() {
    for (const frame of this.querySelectorAll('.mr-preview__frame')) {
      if (frame.querySelector('.mr-preview__hint')) continue;

      const hint = document.createElement('span');
      hint.className = 'mr-preview__hint';
      hint.setAttribute('aria-hidden', 'true');
      hint.innerHTML = HAND_ICON;
      frame.append(hint);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  attributeChangedCallback(name) {
    if (name === 'data-logo' || name === 'data-logo2' || name === 'data-logo3') {
      this.#loadLogo(name.replace('data-', ''));
      return;
    }

    if (name === 'data-zones' || name === 'data-icons' || name.startsWith('data-phrases-')) this.#sync();
    else this.#drawAll();
  }

  /**
   * Zonas que tienen al menos un ícono. El bloque de grabado lo consulta para
   * saber si, grabando virola y costado, hay algo en cada una.
   *
   * @returns {Set<string>}
   */
  get zonesWithIcons() {
    return new Set(this.#iconZones.values());
  }

  /** Escena donde entra el próximo ícono (la elige el bloque de grabado). */
  set targetZone(zone) {
    this.#currentZone = zone || '';
  }

  /**
   * Escena en la que está un ícono agregado.
   *
   * @param {string} uid
   */
  iconZone(uid) {
    return this.#iconZones.get(uid) ?? '';
  }

  /** Avisa al bloque de grabado qué escena tocó el cliente. */
  #announceZone() {
    this.dispatchEvent(new CustomEvent('mr-preview:zone', { bubbles: true, detail: { zone: this.#currentZone } }));
  }

  get #stages() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-mr-stage]')]);
  }

  /** Íconos elegidos, en el orden en que se tocaron. */
  get #iconIds() {
    return (this.dataset.icons || '').split(',').filter(Boolean);
  }

  /**
   * Decide en qué zona va cada ícono. Uno nuevo entra en la última zona que
   * tocó el cliente; si su zona deja de estar (cambió de virola a costado),
   * pasa a la primera que haya.
   *
   * @param {string[]} zones
   */
  #assignIcons(zones) {
    const ids = this.#iconIds;

    for (const id of [...this.#iconZones.keys()]) {
      if (!ids.includes(id)) this.#iconZones.delete(id);
    }

    for (const id of ids) {
      const zone = this.#iconZones.get(id);
      if (zone && zones.includes(zone)) continue;
      this.#iconZones.set(id, zones.includes(this.#currentZone) ? this.#currentZone : zones[0]);
    }
  }

  /** Reparte las piezas entre las escenas y decide cuáles se ven. */
  #sync() {
    const zones = (this.dataset.zones || 'virola').split(',');

    this.#assignIcons(zones);

    for (const stage of this.#stages) {
      const zone = stage.dataset.zone ?? '';
      const active = zones.includes(zone);

      stage.hidden = !active;
      if (!active) {
        this.#items.delete(stage);
        continue;
      }

      // Las frases de su lista y el logo de esa zona
      const list = this.#listOf(zone);
      /** @type {(number | string)[]} */
      const slots = this.#phrases(list).map((phrase) => `${TEXT_PREFIX}${list}:${phrase.id}`);
      if (zone === 'virola') {
        if (this.#ringLogo) slots.push(RING_LOGO_SLOT);
      } else if (isSecond(zone)) {
        if (this.#logo2) slots.push(LOGO2_SLOT);
      } else if (this.#logo) {
        slots.push(LOGO_SLOT);
      }
      for (const [id, iconZone] of this.#iconZones) {
        if (iconZone === zone) slots.push(`${ICON_PREFIX}${id}`);
      }

      this.#items.set(
        stage,
        slots.map((slot) => this.#createItem(stage, slot))
      );
      this.#arrangeTexts(stage);
    }

    this.#drawAll();
  }

  /**
   * Coloca y dimensiona las frases que el cliente todavía no tocó, para que
   * no arranquen encimadas ni más grandes que la zona: en el costado se
   * reparten en altura y se achican hasta entrar; en el aro, cada una se
   * achica hasta caber en su tramo (arriba, abajo, a los lados…). En cuanto
   * el cliente mueve una frase o le cambia el tamaño o el giro, queda donde
   * la puso y esto ya no la toca.
   *
   * @param {HTMLElement} stage
   */
  #arrangeTexts(stage) {
    const zone = stage.dataset.zone ?? '';
    if (zone === 'bombilla') return;

    const texts = (this.#items.get(stage) ?? []).filter((item) => item.type === 'text' && this.#textOf(item.slot));
    if (!texts.length) return;

    const area = areaOf(stage);
    const image = stage.querySelector('.mr-preview__image');
    // Alto/ancho de la foto: los tamaños van en % del alto y las áreas en % del ancho
    const ratio =
      image instanceof HTMLImageElement && image.naturalWidth ? image.naturalHeight / image.naturalWidth : 1;
    const count = texts.length;

    if (zone === 'virola') {
      // Tramo de aro que le toca a cada frase según cuántas hay: con dos,
      // medio aro cada una; con tres o cuatro, un cuarto; con más, un octavo
      const span = (count <= 2 ? Math.PI : count <= 4 ? Math.PI / 2 : Math.PI / 4) * 0.85;
      const radius = (area.inner + area.outer) / 2;

      for (const item of texts) {
        if (item.touched) continue;
        const chars = Math.max(this.#textOf(item.slot).length, 1);
        // Ancho aproximado de una letra: 0,55 del cuerpo de la fuente
        const fit = (span * radius) / (chars * 0.55 * 0.18 * ratio);
        item.size = clamp(Math.floor(fit), 10, 40);
      }
      return;
    }

    // Costado: una franja de alto para cada frase, en el orden de la lista
    const free = texts.filter((item) => !item.touched);
    free.forEach((item, index) => {
      const text = this.#textOf(item.slot);
      const lines = text.split('\n');
      const chars = Math.max(...lines.map((line) => line.length), 1);
      const band = area.height / free.length;

      item.x = area.x + area.width / 2;
      item.y = area.y + band * (index + 0.5);

      const byHeight = (band * 0.85) / (lines.length * 1.2 * 0.18);
      const byWidth = (area.width * 0.95) / (chars * 0.55 * 0.18 * ratio);
      item.size = clamp(Math.floor(Math.min(byHeight, byWidth)), 10, isTermo(zone) ? 20 : 40);
    });
  }

  /** Estado inicial de una pieza: centrada en su área grabable. */
  #createItem(stage, slot) {
    const previous = (this.#items.get(stage) ?? []).find((item) => item.slot === slot);
    if (previous) return previous;

    const area = areaOf(stage);
    const isLogo = slot === LOGO_SLOT || slot === LOGO2_SLOT || slot === RING_LOGO_SLOT;
    const icon = typeof slot === 'string' && slot.startsWith(ICON_PREFIX) ? slot.slice(ICON_PREFIX.length) : '';
    // Orden de la frase en su lista: decide dónde arranca, para no caer encima de otra
    const order = Math.max(0, this.#phraseOrder(slot));

    // Bombilla: todo sobre la caña, centrado. El texto va en vertical; los
    // íconos y el logo, derechos y escalonados a lo largo de la caña.
    if (stage.dataset.zone === 'bombilla') {
      const centerX = area.x + area.width / 2;

      if (icon) {
        const count = Math.max(0, this.#iconIds.indexOf(icon));
        return {
          slot,
          type: 'icon',
          icon,
          x: centerX,
          // A tamaño 40 el ícono ocupa el ancho de la caña
          y: area.y + area.height * (0.06 + 0.11 * (count % 9)),
          size: 40,
        };
      }

      return {
        slot,
        type: isLogo ? 'image' : 'text',
        x: centerX,
        y: area.y + area.height * (isLogo ? 0.88 : 0.5),
        size: isLogo ? 10 : 30,
        facing: 'up',
      };
    }

    if (icon) {
      // Cada ícono se corre según su orden, para no caer encima del anterior
      const count = Math.max(0, this.#iconIds.indexOf(icon));

      if (stage.dataset.zone === 'virola') {
        return {
          slot,
          type: 'icon',
          icon,
          angle: -Math.PI / 2 + (count + 1) * 0.55,
          radius: (area.inner + area.outer) / 2,
          size: 40,
          facing: 'out',
        };
      }

      return {
        slot,
        type: 'icon',
        icon,
        // Tres por fila; con más, la siguiente fila, para que no se tapen
        x: clamp(area.x + area.width * (0.25 + 0.25 * (count % 3)), area.x, area.x + area.width),
        y: area.y + area.height * (0.7 - 0.3 * (Math.floor(count / 3) % 3)),
        size: isTermo(stage.dataset.zone) ? 30 : 40,
      };
    }

    if (stage.dataset.zone === 'virola' && isLogo) {
      // A la izquierda del aro: arriba y abajo van las frases, y los íconos
      // arrancan arriba a la derecha.
      return {
        slot,
        type: 'image',
        angle: Math.PI,
        radius: (area.inner + area.outer) / 2,
        size: 12,
        facing: 'out',
      };
    }

    if (stage.dataset.zone === 'virola') {
      const angle = RING_ANGLES[order % RING_ANGLES.length];
      return {
        slot,
        type: 'text',
        angle,
        radius: (area.inner + area.outer) / 2,
        size: 40,
        // Arriba mirando adentro y abajo mirando afuera: así se leen de
        // entrada en su posición inicial.
        facing: Math.sin(angle) < 0 ? 'in' : 'out',
        shape: 'curve',
        rotation: 0,
      };
    }

    // El cuerpo del termo es angosto: las piezas arrancan más chicas
    const narrow = isTermo(stage.dataset.zone);

    return {
      slot,
      type: isLogo ? 'image' : 'text',
      x: area.x + area.width / 2,
      y: area.y + area.height * (isLogo ? 0.75 : BODY_ROWS[order % BODY_ROWS.length]),
      size: isLogo ? (narrow ? 12 : 25) : narrow ? 20 : 40,
      rotation: 0,
    };
  }

  /**
   * Lista de frases de una escena: la del otro costado, la del costado
   * grabando también la virola, o la principal.
   *
   * @param {string} zone
   */
  #listOf(zone) {
    if (isSecond(zone)) return 's';
    if (zone === 'virola') return 'p';
    const zones = (this.dataset.zones || '').split(',');
    return zones.includes('virola') && (zone === 'cuerpo' || zone === 'termo') ? 'c' : 'p';
  }

  /**
   * Frases de una lista, tal como las manda el bloque de grabado.
   *
   * @param {string} list - «p», «c» o «s»
   * @returns {{ id: string, text: string }[]}
   */
  #phrases(list) {
    const key = /** @type {'phrasesP' | 'phrasesC' | 'phrasesS'} */ (`phrases${list.toUpperCase()}`);
    try {
      const value = JSON.parse(this.dataset[key] || '[]');
      return Array.isArray(value) ? value.filter((phrase) => phrase && phrase.id) : [];
    } catch {
      return [];
    }
  }

  /** @param {number | string} slot */
  #phraseOrder(slot) {
    if (typeof slot !== 'string' || !slot.startsWith(TEXT_PREFIX)) return -1;
    const [list, id] = slot.slice(TEXT_PREFIX.length).split(':');
    return this.#phrases(list).findIndex((phrase) => phrase.id === id);
  }

  /** @param {number | string} slot */
  #textOf(slot) {
    if (typeof slot !== 'string' || !slot.startsWith(TEXT_PREFIX)) return '';
    const [list, id] = slot.slice(TEXT_PREFIX.length).split(':');
    return (this.#phrases(list).find((phrase) => phrase.id === id)?.text || '').trim();
  }

  /**
   * ¿Se puede girar esa pieza? Íconos y logos siempre; el texto, cuando va
   * recto: en el costado siempre, en el aro solo en modo «Recto». En la
   * bombilla el texto va en vertical y tiene su propio botón.
   */
  #canRotate(item, stage) {
    if (!item) return false;
    if (item.type === 'icon' || item.type === 'image') return true;
    const zone = stage.dataset.zone;
    if (zone === 'bombilla') return false;
    return zone !== 'virola' || item.shape === 'straight';
  }

  /**
   * El logo llega como URL de objeto: se carga una vez y se redibuja.
   *
   * @param {string} [which] - «logo», «logo2» (otro costado) o «logo3» (virola).
   */
  #loadLogo(which = 'logo') {
    const key = /** @type {'logo' | 'logo2' | 'logo3'} */ (which);
    const source = this.dataset[key];

    /** @param {HTMLImageElement | null} image */
    const set = (image) => {
      if (key === 'logo2') this.#logo2 = image;
      else if (key === 'logo3') this.#logo3 = image;
      else this.#logo = image;
      this.#sync();
    };

    if (!source) {
      set(null);
      return;
    }

    const image = new Image();
    image.onload = () => {
      // Si mientras cargaba llegó otro, este ya no cuenta
      if (this.dataset[key] === source) set(image);
    };
    image.src = source;
  }

  /** ¿Se graban virola y costado a la vez? */
  get #bothZones() {
    return (this.dataset.zones || '').split(',').includes('cuerpo');
  }

  /**
   * Logo de la virola: el suyo grabando las dos zonas; si no, el de siempre.
   *
   * @returns {HTMLImageElement | null}
   */
  get #ringLogo() {
    return this.#bothZones ? this.#logo3 : this.#logo;
  }

  /** Logo que dibuja una pieza de imagen, según su escena. */
  #logoOf(item) {
    if (item.slot === RING_LOGO_SLOT) return this.#ringLogo;
    return item.slot === LOGO2_SLOT ? this.#logo2 : this.#logo;
  }

  /** Pieza que se está ajustando en esa escena. */
  #activeItem(stage) {
    const items = (this.#items.get(stage) ?? []).filter((item) => this.#isVisible(item));
    return items.find((item) => item.active) ?? items[0] ?? null;
  }

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const stage = target.closest('[data-mr-stage]');
    if (!(stage instanceof HTMLElement)) return;

    this.#currentZone = stage.dataset.zone ?? '';
    this.#announceZone();

    const piece = target.closest('[data-mr-piece]');
    if (piece instanceof HTMLElement) {
      for (const item of this.#items.get(stage) ?? []) item.active = String(item.slot) === piece.dataset.mrPiece;
      this.#draw(stage);
      return;
    }

    const shape = target.closest('[data-mr-shape]');
    if (shape instanceof HTMLElement) {
      const item = this.#activeItem(stage);
      if (item?.type === 'text' && stage.dataset.zone === 'virola') {
        item.shape = shape.dataset.mrShape === 'straight' ? 'straight' : 'curve';
      }
      this.#draw(stage);
      this.#scheduleExport();
      return;
    }

    if (target.closest('[data-mr-flip]')) {
      const item = this.#activeItem(stage);
      if (item && stage.dataset.zone === 'bombilla') {
        // Tres modos en rueda: de abajo arriba, de arriba abajo y letra a letra
        item.facing = item.facing === 'up' ? 'down' : item.facing === 'down' ? 'stack' : 'up';
      } else if (item) {
        // Hacia dónde miran las letras: al exterior del mate o a su interior
        item.facing = item.facing === 'in' ? 'out' : 'in';
      }
      this.#draw(stage);
    }
  };

  #handleControl = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-mr-rotate]')) {
      const stage = target.closest('[data-mr-stage]');
      if (!(stage instanceof HTMLElement)) return;

      // El giro es de la pieza elegida; cerca de 0 vuelve a quedar derecha
      const item = this.#activeItem(stage);
      if (this.#canRotate(item, stage)) {
        let degrees = Number(target.value) || 0;
        if (Math.abs(degrees) <= ROTATE_SNAP) {
          degrees = 0;
          target.value = '0';
        }
        item.rotation = degrees;
        item.touched = true;
      }

      this.#draw(stage);
      this.#scheduleExport();
      return;
    }

    if (!target.matches('[data-mr-size]')) return;

    const stage = target.closest('[data-mr-stage]');
    if (!(stage instanceof HTMLElement)) return;

    // El deslizador cambia el tamaño de la última pieza que se tocó
    const item = this.#activeItem(stage);
    if (item) {
      item.size = Number(target.value);
      item.touched = true;
    }

    // La imagen para el taller también tiene que llevar el tamaño nuevo
    this.#draw(stage);
    this.#scheduleExport();
  };

  #handlePointerDown = (event) => {
    const canvas = event.target;
    if (!(canvas instanceof HTMLCanvasElement)) return;

    const stage = canvas.closest('[data-mr-stage]');
    if (!(stage instanceof HTMLElement)) return;

    this.#currentZone = stage.dataset.zone ?? '';
    this.#announceZone();
    stage.dataset.mrTouched = 'true';

    // Se arrastra la pieza elegida en el selector: con dos frases encimadas,
    // mover «la más cercana» era impredecible.
    const item = this.#activeItem(stage);
    if (!item) return;

    // Desde ahora la coloca el cliente: el reparto automático ya no la mueve
    item.touched = true;

    // Se anota el punto de partida para mover por diferencia: si se colocara
    // la pieza bajo el dedo, al tocar daría un salto.
    this.#dragging = {
      stage,
      item,
      origin: pointIn(canvas, event),
      start: { angle: item.angle, radius: item.radius, x: item.x, y: item.y },
    };

    canvas.setPointerCapture(event.pointerId);
  };

  #handlePointerMove = (event) => {
    if (this.#dragging && event.target instanceof HTMLCanvasElement) this.#moveTo(event.target, event);
  };

  #handlePointerUp = (event) => {
    // Solo al soltar un arrastre en la foto. Si se redibujara en cualquier
    // «pointerup» (p. ej. al tocar un botón de pieza), los botones se
    // rehacían antes del «click» y la elección se perdía.
    if (!this.#dragging) return;

    if (event.target instanceof HTMLCanvasElement) event.target.releasePointerCapture(event.pointerId);
    this.#dragging = null;
    this.#drawAll();
  };

  #isVisible(item) {
    if (item.type === 'icon') return true;
    return item.type === 'image' ? Boolean(this.#logoOf(item)) : Boolean(this.#textOf(item.slot));
  }

  /** Nombre del ícono, tal como lo muestra su botón. */
  #iconName(id) {
    const base = iconBase(id);
    const button = this.closest('mr-engraving')?.querySelector(`[data-mr-icon="${CSS.escape(base)}"]`);
    return button instanceof HTMLElement ? button.dataset.mrIconName || base : base;
  }

  /**
   * El dibujo del ícono como imagen para el lienzo, en el color del grabado
   * de esa zona. Sale del mismo dibujo del botón: uno solo para la pantalla,
   * la previsualización y el taller.
   *
   * @param {string} uid - El ícono, o una copia suya («bloque~n»)
   * @param {string} ink
   * @returns {HTMLImageElement | HTMLCanvasElement | null} La imagen, o null si aún no cargó.
   */
  #iconImage(uid, ink) {
    // Las copias de un mismo ícono comparten dibujo
    const id = iconBase(uid);
    const key = `${id}|${ink}`;
    const cached = this.#iconImages.get(key);
    if (cached instanceof HTMLCanvasElement) return cached;
    // Una imagen propia solo se dibuja ya teñida, nunca con sus colores
    if (cached?.dataset.mrTint) return null;
    if (cached) return cached.complete && cached.naturalWidth ? cached : null;

    const button = this.closest('mr-engraving')?.querySelector(`[data-mr-icon="${CSS.escape(id)}"]`);

    // Imagen propia: se tiñe del color del grabado conservando su silueta
    const source = button?.querySelector('[data-mr-icon-src]');
    if (source instanceof HTMLElement && source.dataset.mrIconSrc) {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.dataset.mrTint = 'true';
      image.onload = () => {
        this.#iconImages.set(key, tint(image, ink));
        this.#drawAll();
      };
      image.src = source.dataset.mrIconSrc;
      this.#iconImages.set(key, image);
      return null;
    }

    const svg = button?.querySelector('svg');
    if (!(svg instanceof SVGElement)) return null;

    const clone = /** @type {SVGElement} */ (svg.cloneNode(true));
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', '512');
    clone.setAttribute('height', '512');
    const markup = new XMLSerializer().serializeToString(clone).replaceAll('currentColor', ink);

    const image = new Image();
    image.onload = () => this.#drawAll();
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
    this.#iconImages.set(key, image);

    return null;
  }

  /** Mueve la pieza lo mismo que se ha movido el dedo, sin salir del área. */
  #moveTo(canvas, event) {
    if (!this.#dragging) return;

    const { stage, item, origin, start } = this.#dragging;
    const point = pointIn(canvas, event);
    const area = areaOf(stage);

    if (stage.dataset.zone === 'virola') {
      // En el aro el desplazamiento se mide en ángulo y en distancia al centro
      const rect = canvas.getBoundingClientRect();
      const ratio = rect.height / rect.width;
      const from = polar(origin, area, ratio);
      const to = polar(point, area, ratio);

      item.angle = start.angle + angleDelta(from.angle, to.angle);
      item.radius = clamp(start.radius + (to.radius - from.radius), area.inner, area.outer);
    } else {
      item.x = clamp(start.x + (point.x - origin.x), area.x, area.x + area.width);
      item.y = clamp(start.y + (point.y - origin.y), area.y, area.y + area.height);
    }

    this.#draw(stage);
  }

  #drawAll = () => {
    for (const stage of this.#stages) {
      if (!stage.hidden) this.#draw(stage);
    }

    this.#scheduleExport();
  };

  /** @param {HTMLElement} stage */
  #draw(stage) {
    const canvas = stage.querySelector('[data-mr-canvas]');
    const image = stage.querySelector('.mr-preview__image');
    if (!(canvas instanceof HTMLCanvasElement) || !(image instanceof HTMLImageElement) || !image.clientWidth) return;

    const dpr = window.devicePixelRatio || 1;
    const width = image.clientWidth;
    const height = image.clientHeight || width;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    if (this.#dragging?.stage === stage) this.#drawArea(ctx, stage, width, height);
    this.#drawItems(ctx, stage, width, height);
    this.#renderPieces(stage);
  }

  /**
   * Botones para elegir qué pieza se ajusta. Se etiquetan con su propio
   * contenido —el principio de la frase, o el nombre del logo— para que no
   * haga falta explicar cuál es cuál.
   *
   * @param {HTMLElement} stage
   */
  #renderPieces(stage) {
    const list = stage.querySelector('[data-mr-pieces]');
    if (!(list instanceof HTMLElement)) return;

    const items = (this.#items.get(stage) ?? []).filter((item) => this.#isVisible(item));
    const active = this.#activeItem(stage);

    // Con una sola pieza no hay nada que elegir, ni nada que explicar
    list.hidden = items.length < 2;

    const hint = stage.querySelector('[data-mr-pieces-label]');
    if (hint instanceof HTMLElement) hint.hidden = list.hidden || !hint.textContent?.trim();

    // Las copias de un mismo ícono se numeran para distinguirlas
    /** @type {Map<string, number>} */
    const totals = new Map();
    for (const item of items) {
      if (item.type === 'icon') totals.set(iconBase(item.icon), (totals.get(iconBase(item.icon)) ?? 0) + 1);
    }
    /** @type {Map<string, number>} */
    const seen = new Map();

    /** @type {{ slot: string, label: string, selected: boolean }[]} */
    const pieces = items.map((item) => {
      let label;
      if (item.type === 'icon') {
        const base = iconBase(item.icon);
        const number = (seen.get(base) ?? 0) + 1;
        seen.set(base, number);
        label = (totals.get(base) ?? 0) > 1 ? `${this.#iconName(item.icon)} ${number}` : this.#iconName(item.icon);
      } else {
        label = item.type === 'image' ? this.dataset.logoLabel || 'Logo' : truncate(this.#textOf(item.slot).replace(/\s*\n\s*/g, ' '));
      }
      return { slot: String(item.slot), label, selected: item === active };
    });

    // Si las piezas son las mismas, se reutilizan los botones y solo se marca
    // la elegida: rehacerlos en cada dibujo (al mover, al cargar una imagen,
    // al girar el celular) podía borrar el botón justo cuando se tocaba.
    const signature = pieces.map((piece) => `${piece.slot}\u0000${piece.label}`).join('\u0001');
    if (list.dataset.mrSignature !== signature || list.children.length !== pieces.length) {
      list.replaceChildren(
        ...pieces.map((piece) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'mr-preview__piece';
          button.dataset.mrPiece = piece.slot;
          button.textContent = piece.label;
          return button;
        })
      );
      list.dataset.mrSignature = signature;
    }

    pieces.forEach((piece, index) => {
      list.children[index]?.toggleAttribute('data-selected', piece.selected);
    });

    const size = stage.querySelector('[data-mr-size]');
    if (size instanceof HTMLInputElement && active) size.value = String(active.size);

    // El giro, para lo que se puede girar, con el valor de la pieza elegida
    const rotateWrap = stage.querySelector('[data-mr-rotate-wrap]');
    if (rotateWrap instanceof HTMLElement) {
      const rotatable = this.#canRotate(active, stage);
      rotateWrap.style.display = rotatable ? '' : 'none';

      const rotate = rotateWrap.querySelector('[data-mr-rotate]');
      if (rotatable && rotate instanceof HTMLInputElement) rotate.value = String(active.rotation ?? 0);
    }

    const flip = stage.querySelector('[data-mr-flip]');
    if (flip instanceof HTMLElement && stage.dataset.zone === 'bombilla') {
      // En la bombilla solo el texto tiene sentido: íconos y logo van derechos
      flip.hidden = !active || active.type !== 'text';

      if (!flip.hidden && active) {
        const labels = (this.dataset.directionLabels || '').split('|');
        flip.textContent =
          active.facing === 'down'
            ? labels[1] || 'Se lee de arriba abajo'
            : active.facing === 'stack'
              ? labels[2] || 'Letra a letra'
              : labels[0] || 'Se lee de abajo arriba';
        flip.dataset.facing = active.facing;
      }
    } else if (flip instanceof HTMLElement) {
      // En el aro todo se da vuelta: hacia fuera o hacia dentro
      flip.hidden = stage.dataset.zone !== 'virola' || !active;

      if (!flip.hidden && active) {
        const labels = (this.dataset.facingLabels || '').split('|');
        flip.textContent = active.facing === 'in' ? labels[1] || 'Mira hacia dentro' : labels[0] || 'Mira hacia fuera';
        flip.dataset.facing = active.facing;
      }
    }

    // Curvo / Recto: solo con una frase del aro elegida
    const shapeWrap = stage.querySelector('[data-mr-shape-wrap]');
    if (shapeWrap instanceof HTMLElement) {
      const isText = stage.dataset.zone === 'virola' && active?.type === 'text';
      shapeWrap.hidden = !isText;
      for (const button of shapeWrap.querySelectorAll('[data-mr-shape]')) {
        if (!(button instanceof HTMLElement)) continue;
        button.setAttribute('aria-pressed', String(isText && (active.shape || 'curve') === button.dataset.mrShape));
      }
    }

    // Sobre una pieza circular, una de las dos orientaciones se ve cabeza
    // abajo según en qué punto del aro esté. No es un fallo del dibujo, así
    // que se avisa justo cuando ocurre para que no lo parezca.
    const note = stage.querySelector('[data-mr-facing-note]');
    if (note instanceof HTMLElement) {
      // Recta: se ve cabeza abajo si, con el giro sumado, queda mirando hacia abajo
      const straightAngle =
        active && active.angle + Math.PI / 2 + (active.facing === 'out' ? Math.PI : 0) + ((active.rotation || 0) * Math.PI) / 180;
      const upright =
        active &&
        (active.shape === 'straight'
          ? Math.cos(straightAngle) > -0.3
          : active.facing === 'out'
            ? Math.sin(active.angle) > 0
            : Math.sin(active.angle) < 0);
      note.hidden =
        stage.dataset.zone !== 'virola' ||
        !active ||
        active.type !== 'text' ||
        Boolean(upright);
      note.textContent = this.dataset.facingNote ?? '';
    }
  }

  /** El área grabable solo se dibuja mientras se arrastra, como guía. */
  #drawArea(ctx, stage, width, height) {
    const area = areaOf(stage);

    ctx.save();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.setLineDash([6, 5]);
    ctx.lineWidth = 1;

    if (stage.dataset.zone === 'virola') {
      for (const radius of [area.inner, area.outer]) {
        ctx.beginPath();
        ctx.arc((area.cx / 100) * width, (area.cy / 100) * height, (radius / 100) * width, 0, Math.PI * 2);
        ctx.stroke();
      }
    } else {
      ctx.strokeRect(
        (area.x / 100) * width,
        (area.y / 100) * height,
        (area.width / 100) * width,
        (area.height / 100) * height
      );
    }

    ctx.restore();
  }

  /**
   * Dibuja las piezas de una escena. Es la misma función para la pantalla y
   * para la imagen que recibe el taller: solo cambia el tamaño del lienzo.
   */
  #drawItems(ctx, stage, width, height) {
    const area = areaOf(stage);
    const ring = stage.dataset.zone === 'virola';
    const ink = inkOf(stage);

    for (const item of this.#items.get(stage) ?? []) {
      if (!this.#isVisible(item)) continue;

      if (item.type === 'icon') {
        const image = this.#iconImage(item.icon, ink);
        if (!image) continue;

        // El lado mayor ocupa el tamaño elegido; el otro guarda la proporción
        const side = (item.size / 100) * width * ICON_SCALE;
        const imageWidth = image instanceof HTMLCanvasElement ? image.width : image.naturalWidth;
        const imageHeight = image instanceof HTMLCanvasElement ? image.height : image.naturalHeight;
        const scale = side / Math.max(imageWidth, imageHeight, 1);
        const drawWidth = imageWidth * scale;
        const drawHeight = imageHeight * scale;

        ctx.save();
        if (ring) {
          // En el aro el ícono se apoya en la curva: su base mira al centro,
          // o al revés si el cliente lo dio vuelta
          const radius = (item.radius / 100) * width;
          ctx.translate(
            (area.cx / 100) * width + radius * Math.cos(item.angle),
            (area.cy / 100) * height + radius * Math.sin(item.angle)
          );
          ctx.rotate(item.angle + Math.PI / 2 + (item.facing === 'in' ? Math.PI : 0));
        } else {
          ctx.translate((item.x / 100) * width, (item.y / 100) * height);
        }
        // Giro elegido por el cliente, sumado al del aro
        if (item.rotation) ctx.rotate((item.rotation * Math.PI) / 180);
        ctx.drawImage(image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
        ctx.restore();
        continue;
      }

      const logo = item.type === 'image' ? this.#logoOf(item) : null;
      if (logo && ring) {
        // En el aro el logo se apoya en la curva, como los íconos: su base
        // mira al centro, o al revés si el cliente lo dio vuelta
        const logoWidth = (item.size / 100) * width;
        const logoHeight = (logoWidth * logo.naturalHeight) / logo.naturalWidth;
        const radius = (item.radius / 100) * width;

        ctx.save();
        ctx.translate(
          (area.cx / 100) * width + radius * Math.cos(item.angle),
          (area.cy / 100) * height + radius * Math.sin(item.angle)
        );
        ctx.rotate(item.angle + Math.PI / 2 + (item.facing === 'in' ? Math.PI : 0));
        // Giro elegido por el cliente, sumado al del aro
        if (item.rotation) ctx.rotate((item.rotation * Math.PI) / 180);
        ctx.drawImage(logo, -logoWidth / 2, -logoHeight / 2, logoWidth, logoHeight);
        ctx.restore();
        continue;
      }

      if (logo) {
        const logoWidth = (item.size / 100) * width;
        const logoHeight = (logoWidth * logo.naturalHeight) / logo.naturalWidth;

        ctx.save();
        ctx.translate((item.x / 100) * width, (item.y / 100) * height);
        if (item.rotation) ctx.rotate((item.rotation * Math.PI) / 180);
        ctx.drawImage(logo, -logoWidth / 2, -logoHeight / 2, logoWidth, logoHeight);
        ctx.restore();
        continue;
      }

      const text = this.#textOf(item.slot);
      const fontSize = (item.size / 100) * height * 0.18;

      ctx.save();
      ctx.font = `${fontSize}px ${this.dataset.font || 'serif'}`;
      ctx.fillStyle = ink;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (stage.dataset.zone === 'bombilla' && item.facing === 'stack') {
        // Letra a letra: cada letra derecha, una debajo de otra. Si la columna
        // no entra en la caña, la letra se achica hasta que entre.
        const chars = [...text.replace(/\s*\n\s*/g, ' ')];
        const fit = ((area.height / 100) * height) / Math.max(chars.length * 1.05, 1);
        const size = Math.min(fontSize, fit);
        const step = size * 1.05;
        const top = (item.y / 100) * height - ((chars.length - 1) * step) / 2;

        ctx.font = `${size}px ${this.dataset.font || 'serif'}`;
        chars.forEach((char, index) => {
          ctx.fillText(char, (item.x / 100) * width, top + index * step);
        });
      } else if (stage.dataset.zone === 'bombilla') {
        // Bombilla: una sola línea en vertical, a lo largo de la caña. «up» se
        // lee de abajo hacia arriba; «down», de arriba hacia abajo.
        ctx.translate((item.x / 100) * width, (item.y / 100) * height);
        ctx.rotate(item.facing === 'down' ? Math.PI / 2 : -Math.PI / 2);
        ctx.fillText(text.replace(/\s*\n\s*/g, ' '), 0, 0);
      } else if (ring && item.shape === 'straight') {
        // Recto en el aro: las letras en línea recta, apoyadas en su punto del
        // aro y orientadas como lo estaría la frase curva (así no se sale de
        // la banda al moverla). «Mira hacia fuera/dentro» la da vuelta y el
        // giro la inclina desde ahí: a 90° queda atravesando el aro.
        const radius = (item.radius / 100) * width;
        ctx.translate(
          (area.cx / 100) * width + radius * Math.cos(item.angle),
          (area.cy / 100) * height + radius * Math.sin(item.angle)
        );
        ctx.rotate(item.angle + Math.PI / 2 + (item.facing === 'out' ? Math.PI : 0));
        if (item.rotation) ctx.rotate((item.rotation * Math.PI) / 180);
        ctx.fillText(text.replace(/\s*\n\s*/g, ' '), 0, 0);
      } else if (ring) {
        // El texto sigue el aro; abajo se voltea para poder leerse
        const radius = (item.radius / 100) * width;

        drawArcText(
          ctx,
          text.replace(/\s*\n\s*/g, ' '),
          (area.cx / 100) * width,
          (area.cy / 100) * height,
          radius,
          item.angle,
          item.facing === 'out'
        );
      } else {
        // En el cuerpo el texto va recto, respeta los saltos de línea y gira
        // entero alrededor de su centro
        const lines = text.split('\n');
        const step = fontSize * 1.2;
        const top = -((lines.length - 1) * step) / 2;

        ctx.translate((item.x / 100) * width, (item.y / 100) * height);
        if (item.rotation) ctx.rotate((item.rotation * Math.PI) / 180);
        lines.forEach((line, index) => {
          ctx.fillText(line, 0, top + index * step);
        });
      }

      ctx.restore();
    }
  }

  /** La imagen para el taller se regenera al parar, no en cada píxel. */
  #scheduleExport() {
    clearTimeout(this.#exportTimer);
    this.#exportTimer = window.setTimeout(() => this.#exportDesign(), 400);
  }

  /**
   * Compone las escenas visibles —foto y piezas— en un solo lienzo y lo
   * adjunta al formulario como archivo. La foto se recarga con crossOrigin
   * para que el lienzo no quede «manchado»; si aun así el navegador lo impide,
   * se exporta solo lo dibujado antes que quedarse sin nada.
   */
  async #exportDesign() {
    const input = this.querySelector('[data-mr-design]');
    const stages = this.#stages.filter((stage) => !stage.hidden);
    const hasText = ['p', 'c', 's'].some((list) => this.#phrases(list).some((phrase) => (phrase.text || '').trim()));
    const hasContent = hasText || this.#logo || this.#logo2 || this.#logo3 || this.#iconIds.length > 0;
    if (!(input instanceof HTMLInputElement) || !stages.length || !hasContent) return;

    await this.#ensureFont();

    const canvas = document.createElement('canvas');
    const ratios = stages.map((stage) => {
      const image = stage.querySelector('.mr-preview__image');
      return (image?.naturalHeight ?? 1) / (image?.naturalWidth ?? 1) || 1;
    });

    canvas.width = EXPORT_WIDTH;
    canvas.height = Math.round(EXPORT_WIDTH * ratios.reduce((sum, ratio) => sum + ratio, 0));

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // El JPG no tiene transparencia: sin un fondo, lo que no cubra la foto
    // saldría negro y el grabado oscuro no se leería.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    let offset = 0;
    for (const [index, stage] of stages.entries()) {
      const height = Math.round(EXPORT_WIDTH * ratios[index]);
      const image = stage.querySelector('.mr-preview__image');

      ctx.save();
      ctx.translate(0, offset);

      try {
        if (image instanceof HTMLImageElement) {
          ctx.drawImage(await loadImage(image.currentSrc || image.src), 0, 0, EXPORT_WIDTH, height);
        }
      } catch {
        /* sin foto de fondo, las piezas solas */
      }

      this.#drawItems(ctx, stage, EXPORT_WIDTH, height);
      ctx.restore();

      offset += height;
    }

    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        const transfer = new DataTransfer();
        transfer.items.add(new File([blob], EXPORT_NAME, { type: EXPORT_TYPE }));
        input.files = transfer.files;
      },
      EXPORT_TYPE,
      EXPORT_QUALITY
    );
  }

  /** Espera a la tipografía elegida: sin esto el lienzo dibujaría con otra. */
  async #ensureFont() {
    try {
      await document.fonts.load(`48px ${this.dataset.font || 'serif'}`);
    } catch {
      /* si la fuente no se puede precargar, se dibuja con la de respaldo */
    }
  }
}

/** ¿Es la escena del otro costado («cuerpo2», «termo2»)? @param {string | undefined} zone */
function isSecond(zone) {
  return Boolean(zone?.endsWith('2'));
}

/** El termo y su otro costado: cuerpo angosto. @param {string | undefined} zone */
function isTermo(zone) {
  return zone === 'termo' || zone === 'termo2';
}

/** Área grabable de una escena, en porcentaje de la foto. */
function areaOf(stage) {
  const [a = 50, b = 50, c = 20, d = 30] = (stage.dataset.geometry || '').split(',').map((value) => Number(value.trim()));

  return stage.dataset.zone === 'virola'
    ? { cx: a, cy: b, inner: c, outer: d }
    : { x: a, y: b, width: c, height: d };
}

/** Color del grabado de esa zona; sale de los tokens, no de un valor fijo. */
function inkOf(stage) {
  return getComputedStyle(stage).getPropertyValue('--mr-preview-ink').trim() || '#000000';
}

/** Punto en coordenadas polares respecto al centro del aro. */
function polar(point, area, ratio) {
  const dx = point.x - area.cx;
  const dy = (point.y - area.cy) * ratio;

  return { angle: Math.atan2(dy, dx), radius: Math.hypot(dx, dy) };
}

/** Diferencia de ángulo más corta, para que no pegue una vuelta entera. */
function angleDelta(from, to) {
  let delta = to - from;

  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;

  return delta;
}

function pointIn(canvas, event) {
  const rect = canvas.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) / rect.width) * 100,
    y: ((event.clientY - rect.top) / rect.height) * 100,
  };
}

/**
 * Texto siguiendo un arco: cada letra se gira su propio ancho alrededor del
 * centro, de modo que el renglón se curva con el aro.
 *
 * `flip` cambia hacia dónde miran las letras: sin él los remates se alejan del
 * centro, con él lo miran. En ese caso hay que hacer dos cosas a la vez, y
 * hacer solo una es lo que rompía la palabra:
 *
 *   1. girar cada letra 180°, para que mire al centro
 *   2. recorrer el arco en sentido contrario, para que el orden de lectura se
 *      conserve al mirar la pieza desde el otro lado
 *
 * Girar el marco entero, en cambio, además de invertir el orden llevaba el
 * texto al lado opuesto del aro.
 */
function drawArcText(ctx, text, cx, cy, radius, centerAngle, flip) {
  const chars = [...text];
  const widths = chars.map((char) => ctx.measureText(char).width);
  const total = widths.reduce((sum, width) => sum + width, 0);
  const totalAngle = total / radius;
  const direction = flip ? -1 : 1;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(centerAngle + Math.PI / 2);
  ctx.rotate((-direction * totalAngle) / 2);

  for (let i = 0; i < chars.length; i++) {
    const step = (direction * widths[i]) / radius;

    ctx.rotate(step / 2);
    ctx.save();
    ctx.translate(0, -radius);
    if (flip) ctx.rotate(Math.PI);
    ctx.fillText(chars[i], 0, 0);
    ctx.restore();
    ctx.rotate(step / 2);
  }

  ctx.restore();
}

/**
 * Copia de la imagen pintada de un solo color: conserva la silueta (lo que no
 * es transparente) y le pone el color del grabado.
 *
 * @param {HTMLImageElement} image
 * @param {string} ink
 */
function tint(image, ink) {
  const scale = 512 / Math.max(image.naturalWidth, image.naturalHeight, 1);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = ink;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  return canvas;
}

/** @param {string} src */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

/**
 * El ícono de una copia: «bloque~3» → «bloque».
 *
 * @param {string} uid
 */
function iconBase(uid) {
  return uid.split('~')[0];
}

/** @param {string} text */
function truncate(text) {
  return text.length > 24 ? `${text.slice(0, 23)}…` : text;
}

/** @param {number} value @param {number} min @param {number} max */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

if (!customElements.get('mr-preview')) {
  customElements.define('mr-preview', MrPreview);
}
